import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

const ACCEPT_MESSAGE = `This trade has been accepted! 🎉

To complete the exchange, share your contact information with each other and agree on the specific terms of the trade.

Please note: FishinWiseGuys LLC facilitates connections between collectors but is not a party to any transaction and cannot be held responsible for the outcome of trades arranged through this platform.`

const DECLINE_MESSAGE = `This trade offer has been declined.`

const CANCEL_MESSAGE = `This trade offer has been cancelled by the proposer.`

const STATUS_LABELS = {
  pending:   { label: 'Pending',   cls: 'trade-status-pending'   },
  accepted:  { label: 'Accepted',  cls: 'trade-status-accepted'  },
  declined:  { label: 'Declined',  cls: 'trade-status-declined'  },
  cancelled: { label: 'Cancelled', cls: 'trade-status-cancelled' },
}

function TradeOfferCard({ tradeOffer, conversationId, currentUserId, onStatusChange }) {
  const [acting, setActing] = useState(false)

  if (!tradeOffer) return null

  const isRecipient = tradeOffer.recipient_id === currentUserId
  const isProposer  = tradeOffer.proposer_id  === currentUserId
  const isPending   = tradeOffer.status === 'pending'

  const offeringItems  = tradeOffer.trade_offer_items?.filter(i => i.side === 'offering')  || []
  const requestingItems = tradeOffer.trade_offer_items?.filter(i => i.side === 'requesting') || []

  const statusMeta = STATUS_LABELS[tradeOffer.status] || STATUS_LABELS.pending

  const act = async (newStatus, systemMsg) => {
    setActing(true)
    try {
      const { error: updateErr } = await supabase
        .from('trade_offers')
        .update({ status: newStatus })
        .eq('trade_offer_id', tradeOffer.trade_offer_id)
      if (updateErr) throw updateErr

      await supabase.from('messages').insert({
        conversation_id: parseInt(conversationId),
        sender_id: currentUserId,
        body: systemMsg,
        is_system_message: true,
      })

      onStatusChange()
    } catch (err) {
      console.error('Trade action error:', err)
    } finally {
      setActing(false)
    }
  }

  const renderItem = (item) => {
    const inv = item.user_lure_inventory
    const v   = inv?.lure_variants
    const lure = v?.base_lures
    return (
      <div key={item.offer_item_id} className="trade-card-item">
        {v?.image_url && <img src={v.image_url} alt={v.color_name} className="trade-card-thumb" />}
        <div className="trade-card-item-text">
          <span className="trade-card-lure">{lure?.name || '—'}</span>
          <span className="trade-card-color">{v?.variant_name || v?.color_name || '—'}</span>
          {lure?.brands?.brand_name && (
            <span className="trade-card-brand">{lure.brands.brand_name}</span>
          )}
        </div>
      </div>
    )
  }

  // From the current user's perspective:
  // proposer "offers" things (side=offering) and "requests" things (side=requesting)
  const iOffer    = isProposer ? offeringItems  : requestingItems
  const iReceive  = isProposer ? requestingItems : offeringItems

  return (
    <div className="trade-offer-card">
      <div className="trade-offer-card-header">
        <span className="trade-offer-badge">📦 Trade Offer</span>
        <span className={`trade-status-badge ${statusMeta.cls}`}>{statusMeta.label}</span>
      </div>

      <div className="trade-offer-sides">
        <div className="trade-offer-side">
          <h4>{isProposer ? 'You Offer' : 'They Offer'}</h4>
          {iOffer.length === 0
            ? <p className="trade-card-none">Nothing</p>
            : iOffer.map(renderItem)
          }
        </div>
        <div className="trade-offer-arrow">⇄</div>
        <div className="trade-offer-side">
          <h4>{isProposer ? 'You Receive' : 'You Receive'}</h4>
          {iReceive.length === 0
            ? <p className="trade-card-none">Nothing</p>
            : iReceive.map(renderItem)
          }
        </div>
      </div>

      {isPending && isRecipient && (
        <div className="trade-offer-actions">
          <button
            className="btn-danger"
            onClick={() => act('declined', DECLINE_MESSAGE)}
            disabled={acting}
          >
            {acting ? '…' : 'Decline'}
          </button>
          <button
            className="btn-success"
            onClick={() => act('accepted', ACCEPT_MESSAGE)}
            disabled={acting}
          >
            {acting ? '…' : 'Accept Trade'}
          </button>
        </div>
      )}

      {isPending && isProposer && (
        <div className="trade-offer-actions">
          <button
            className="btn-danger"
            onClick={() => act('cancelled', CANCEL_MESSAGE)}
            disabled={acting}
          >
            {acting ? '…' : 'Cancel Offer'}
          </button>
        </div>
      )}
    </div>
  )
}

function Conversation() {
  const { conversationId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const threadRef = useRef(null)

  const [messages, setMessages] = useState([])
  const [otherUser, setOtherUser] = useState(null)
  const [tradeOffer, setTradeOffer] = useState(null)
  const [loading, setLoading] = useState(true)

  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')

  useEffect(() => {
    if (!user) return
    fetchData()
  }, [conversationId, user])

  useEffect(() => {
    if (!user || messages.length === 0) return
    markAsRead()
  }, [messages])

  useEffect(() => {
    const el = threadRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages])

  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel(`conversation-${conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      }, () => { fetchMessages() })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [conversationId, user])

  const fetchData = async () => {
    try {
      const { data: conv, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('conversation_id', conversationId)
        .single()

      if (error || !conv) { navigate('/messages'); return }

      const otherId = conv.participant_1 === user.id ? conv.participant_2 : conv.participant_1
      const { data: otherData } = await supabase
        .from('users')
        .select('id, username')
        .eq('id', otherId)
        .single()

      setOtherUser(otherData)

      if (conv.trade_id) {
        await fetchTradeOffer(conv.trade_id)
      }

      await fetchMessages()
    } catch (err) {
      console.error('Error loading conversation:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchTradeOffer = async (tradeOfferId) => {
    const { data, error } = await supabase
      .from('trade_offers')
      .select(`
        *,
        trade_offer_items (
          offer_item_id, side,
          user_lure_inventory (
            inventory_id, user_id, quantity,
            lure_variants (
              variant_id, color_name, variant_name, image_url,
              base_lures (
                base_lure_id, name,
                brands (brand_name)
              )
            )
          )
        )
      `)
      .eq('trade_offer_id', tradeOfferId)
      .single()

    if (!error) setTradeOffer(data)
  }

  const fetchMessages = async () => {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
    if (!error) setMessages(data || [])
  }

  const markAsRead = async () => {
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', conversationId)
      .eq('is_read', false)
      .neq('sender_id', user.id)
  }

  const handleSend = async () => {
    if (!body.trim()) return
    setSending(true)
    setSendError('')
    const { error } = await supabase
      .from('messages')
      .insert({ conversation_id: parseInt(conversationId), sender_id: user.id, body: body.trim() })
    if (error) {
      setSendError('Failed to send message.')
    } else {
      setBody('')
      await fetchMessages()
    }
    setSending(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleTradeStatusChange = () => {
    if (tradeOffer) fetchTradeOffer(tradeOffer.trade_offer_id)
    fetchMessages()
  }

  if (!user) return <div className="loading">Loading...</div>
  if (loading) return <div className="loading">Loading conversation...</div>

  return (
    <div className="conversation-page">
      <div className="conversation-page-header">
        <Link to="/messages" className="conversation-back">← Messages</Link>
        <div className="conversation-page-title">
          {otherUser ? (
            <Link to={`/profile/${otherUser.id}`} className="conversation-other-name">
              {otherUser.username}
            </Link>
          ) : 'Conversation'}
        </div>
      </div>

      {/* Trade offer card (pinned above messages) */}
      {tradeOffer && (
        <TradeOfferCard
          tradeOffer={tradeOffer}
          conversationId={conversationId}
          currentUserId={user.id}
          onStatusChange={handleTradeStatusChange}
        />
      )}

      {/* Message thread */}
      <div className="conversation-thread" ref={threadRef}>
        {messages.length === 0 ? (
          <p className="conversation-empty">No messages yet. Say hello to {otherUser?.username}!</p>
        ) : (
          messages.map(msg => {
            if (msg.is_system_message) {
              return (
                <div key={msg.message_id} className="system-message-wrap">
                  <div className="system-message-bubble">
                    {msg.body.split('\n').map((line, i) => (
                      <p key={i} style={{ margin: line ? '0.2rem 0' : '0.5rem 0' }}>{line || <br />}</p>
                    ))}
                    <span className="message-time">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' · '}
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )
            }

            if (msg.is_trade_offer) {
              // The initial trade offer message — show condensed in thread
              return (
                <div key={msg.message_id} className="message-bubble-wrap trade-offer-message-wrap">
                  <div className="trade-offer-inline-bubble">
                    <span className="trade-offer-inline-label">📦 Trade Offer sent</span>
                    <span className="message-time">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' · '}
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )
            }

            const isMine = msg.sender_id === user.id
            return (
              <div key={msg.message_id} className={`message-bubble-wrap${isMine ? ' mine' : ' theirs'}`}>
                <div className={`message-bubble${isMine ? ' bubble-mine' : ' bubble-theirs'}`}>
                  <p className="message-body">{msg.body}</p>
                  <span className="message-time">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' · '}
                    {new Date(msg.created_at).toLocaleDateString()}
                    {isMine && (
                      <span className="message-read-indicator">{msg.is_read ? ' ✓✓' : ' ✓'}</span>
                    )}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Reply box */}
      <div className="conversation-reply-box">
        {sendError && <div className="alert-error" style={{ marginBottom: '0.5rem' }}>{sendError}</div>}
        <div className="conversation-reply-row">
          <textarea
            className="conversation-reply-input"
            value={body}
            onChange={e => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${otherUser?.username || ''}… (Enter to send, Shift+Enter for new line)`}
            rows="2"
            disabled={sending}
          />
          <button
            className="btn-primary conversation-send-btn"
            onClick={handleSend}
            disabled={sending || !body.trim()}
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Conversation
